import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import TextBlock from '@/components/TextBlock'
import YoutubeVideo from '@/components/YoutubeVideo'
import RevealGrid from '@/components/RevealGrid'
import Footer from '@/components/Footer'
import BtnLink from '@/components/BtnLink'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { insertBrAfterFirstSpace } from '@/utils/strings'
import style from './style'

const useStyles = createUseStyles(style)

const Group = ({
  stub,
}) => {
  const { getImageUrl, setPageAnimationReady } = useContext(GeneralContext)
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, strings, mediaQueryKey } = useSelector((state) => ({
    current: state.page.current,
    mediaQueryKey: state.mediaQuery.key,
    page: state.page.pages[stub] || {},
    ready: state.loading.ready,
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub, 'pages')
    preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) preloadMedia()
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Cover Home
  ------------------------------*/
  const renderCoverHome = () => {
    return (page.featured_media && page.title.rendered) && (
      <Hero
        aboveTheFold={true}
        background="blue"
        img={(mediaQueryKey <= 1 && page.featured_media_mobile?.full[0]) ? page.featured_media_mobile?.full[0] : getImageUrl(page.featured_media, 'full', stub, 'pages')}
        text={page.content.rendered}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        fontSize="lg"
        color="light"
        home
      />
    )
  }

  /*------------------------------
  Render Intro
  ------------------------------*/
  const renderIntroText = () => {
    return (
      <div className={`${classes.wrapper} ${classes.intro}`}>
        <TextBlock
          title={page.banner_single_title}
          text={page.banner_single_text}
        />
      </div>
    )
  }

  /*------------------------------
  Render Additional Text
  ------------------------------*/
  const renderAdditionalText = () => {
    return (
      <div className={`${classes.wrapper} ${classes.intro}`}>
        <TextBlock
          className={classes.additionalText}
          title={page.banner_single_title2}
          text={page.banner_single_text2}
        />
      </div>
    )
  }

  /*------------------------------
  Render Highlights
  ------------------------------*/
  const renderHighlights = () => {
    return (
      <RevealGrid
        className={`${classes.wrapper} ${classes.highlights}`}
      >
        {[
          { value: page.highlight_siti, label: strings.label_siti },
          { value: page.highlight_risorse, label: strings.label_risorse },
          { value: page.highlight_fatturato, label: strings.label_fatturato },
        ].map((item, i) => (
          <div
            key={i.toString()}
            className={classes.highlightItem}
          >
            {item.value}
            <span>{item.label}</span>
          </div>
        ))}
      </RevealGrid>
    )
  }

  /*------------------------------
  Render Simple Text
  ------------------------------*/
  const renderSimpleText = (el, i) => {
    return (
      <div key={i.toString()} className={classes.wrapper}>
        <TextBlock
          pretitle={el.company_pretitle}
          text={el.company_text}
          label={el.company_label}
          link={el.company_link}
          btnLink
        />
      </div>
    )
  }

  /*------------------------------
  Render Big Text
  ------------------------------*/
  const renderBigText = (el, i) => {
    return (
      <div key={i.toString()} className={classes.wrapper}>
        <TextBlock
          pretitle={el.company_pretitle}
          title={el.company_title}
          text={el.company_text}
        />
      </div>
    )
  }

  /*------------------------------
  Render Big Text
  ------------------------------*/
  const renderColumnText = (el, i) => {
    return (
      <div key={i.toString()} className={`${classes.wrapper} ${classes.column}`}>
        <TextBlock
          text={el.company_text}
        />
      </div>
    )
  }

  /*------------------------------
  Render Video
  ------------------------------*/
  const renderVideo = (el, i) => {
    return (
      <div key={i} className={classes.wrapper}>
        <YoutubeVideo
          videoId={el.company_video_id}
          poster={getImageUrl(el.company_img, 'full', stub, 'pages')}
        />
      </div>
    )
  }

  /*------------------------------
  Render Image
  ------------------------------*/
  const renderImage = (el, i) => {
    return (
      <div key={i} className={classes.wrapper}>
        <img
          className={classes.image}
          src={(mediaQueryKey <= 1 && getImageUrl(el.company_img_mobile, 'full', stub, 'pages')) ? getImageUrl(el.company_img_mobile, 'full', stub, 'pages') : getImageUrl(el.company_img, 'full', stub, 'pages')}
          alt=""
        />
      </div>
    )
  }

  /*------------------------------
  Render List Images
  ------------------------------*/
  const renderListImages = (el, i) => {
    return (
      <RevealGrid
        key={i}
        className={`${classes.wrapper} ${classes.logoWrapper}`}
      >
        {[
          { imgID: el.company_list_img_1, link: el?.company_list_link_1, file: el?.company_list_file_1 },
          { imgID: el.company_list_img_2, link: el?.company_list_link_2, file: el?.company_list_file_2 },
          { imgID: el.company_list_img_3, link: el?.company_list_link_3, file: el?.company_list_file_3 },
        ].map((item, ind) => {
          const Tag = item.link ? 'a' : 'span'
          return (
            <div
              className={classes.listItem}
              key={ind.toString()}
            >
              <Tag
                {...{
                  ...item.link && {
                    href: item.link,
                    target: '_blank',
                  },
                }}
              >
                <img
                  key={item.imgID}
                  className={classes.logos}
                  src={getImageUrl(item.imgID, 'large', stub, 'pages')}
                  alt=""
                />
              </Tag>
              {item.file && (
                <BtnLink
                  label={strings.download_label}
                  link={item.file}
                  theme="light"
                />
              )}
            </div>
          )
        })}
      </RevealGrid>
    )
  }

  /*------------------------------
  Render Page Elements
  ------------------------------*/
  const renderPageElements = (pageElements) => {
    return pageElements && pageElements.map((element, i) => {
      if (element.layout_choice === 'simple_text') return renderSimpleText(element, i)
      if (element.layout_choice === 'big_text') return renderBigText(element, i)
      if (element.layout_choice === 'column_text') return renderColumnText(element, i)
      if (element.layout_choice === 'image') return renderImage(element, i)
      if (element.layout_choice === 'video') return renderVideo(element, i)
      if (element.layout_choice === 'list') return renderListImages(element, i)
      return null
    })
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverHome()}
        {renderIntroText()}
        {renderAdditionalText()}
        {renderHighlights()}
        <div className={classes.pageElements}>
          {renderPageElements(page.page_builder_choice_company)}
        </div>
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageGroup ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Group
